import * as React from "react";
import {action, computed, observable, toJS} from "mobx";
import {observer} from "mobx-react";
import {HRFormBlock} from "components/hr/main/email-tool/HRFormBlock";
import {HRCustomRecipients} from "components/hr/main/email-tool/HRCustomRecipients";
import './HREmailTool.pcss';
import {UIState} from "data/UIState";
import {HRToolCard} from "components/hr/widgets/HRToolCard";
import {IRecipient, IUserSelectorData, RecipientModeType} from "data/hr/HREmailData";

const EmailType = {
  INVITE_PLATFORM:'invite-platform',
  INVITE_SOLUTION:'invite-solution',
  CUSTOM:'custom',
}

@observer
export class HREmailTool extends React.Component {

  @observable sending = false;
  @observable emailType;
  @observable recipients:IRecipient[] = [];
  @observable subject = '';
  @observable body = '';
  @observable solutionId:string = '';
  @observable locationId:string = '';
  @observable userSelectorMode:RecipientModeType = 'all';
  @observable templateName = '';

  @action setCustomType = () => {
    this.emailType=EmailType.CUSTOM;
    this.userSelectorMode = 'all';
    this.subject = "";
    this.body = "";
    this.solutionId = '';
    this.locationId = '';
    this.templateName = 'hr-custom.template';
  }
  @action setInvitePlatformType = () => {
    this.emailType=EmailType.INVITE_PLATFORM;
    this.userSelectorMode = 'manual';
    this.solutionId = '';
    this.locationId = '';
    this.templateName = 'hr-platform.template';
  }
  @action setInviteSolutionType = () => {
    this.emailType=EmailType.INVITE_SOLUTION;
    this.userSelectorMode = 'not-solution';
    this.solutionId = '';
    this.locationId = '';
    this.templateName = 'hr-solution.template';
  }
  @computed get isCustomType() {
    return this.emailType===EmailType.CUSTOM
  }
  @computed get isInvitePlatformType() {
    return this.emailType===EmailType.INVITE_PLATFORM
  }
  @computed get isInviteSolutionType() {
    return this.emailType===EmailType.INVITE_SOLUTION
  }

  @computed get locations() {
    if(this.solutionId){
      console.log(toJS(UIState.dashboardData.data.solutions));
      const solution = UIState.dashboardData.data.solutions.find((solution)=>solution._id===this.solutionId);
      if(solution) {
        return UIState.settings.LOCATIONS.filter((location)=>solution.locations.includes(location._id));
      }
    }
    return [];
  }



  @action send = () => {
    if(!this.sending) {
      this.sending = true;
      if (confirm('Have you double checked everything? If you confirm, the email will be sent immediately.')) {

        // build location id array
        let locationIds:string[] = [];
        if(this.isInviteSolutionType) {
          if(this.locations.length==1) {
            locationIds = [this.locations[0]._id];
          } else {
            const solution = UIState.dashboardData.data.solutions.find((solution)=>solution._id===this.solutionId);
            if(solution) {
              locationIds = this.locations.map((location)=>location._id);
            }
          }
        }

        // compatibility with admin email sender type
        const userSelectorData: IUserSelectorData = {
          userSelectorMode: this.userSelectorMode,
          solutionId: this.solutionId,
          userIds: [],
          userSelectorIsValid: true,
          manualRecipients:this.recipients,
          locationIds: locationIds,
        }

        //Build the email send request
        const request = {
          template: this.templateName,
          userSelectorData: userSelectorData,
          additionalVariables: {}
        }
        if (this.isCustomType) {
          request.additionalVariables['subject'] = this.subject;
          request.additionalVariables['body'] = this.body;
        }
        UIState.hrOpenPleaseWait();
        UIState.emailData.send(request)
        .then(() => {
          UIState.hrOpenAlert("Message Sent", "A copy was saved to your Email History.");
          this.clear();
        })
        .catch((err) => {
          const name = err && err.name ? err.name : "Message Failed";
          const description = err && err.description ? err.description : "Something went wrong when sending the message. Please make sure that your Internet connection is stable when sending. If this problem happens again, please contact Wellness Concepts for help.";
          UIState.hrOpenAlert(name, description);
          this.sending = false;
        });
      } else {
        this.sending = false;
      }
    }
  }

  @action clear = () => {
    this.subject = '';
    this.body = '';
    this.sending = false;
    this.solutionId = '';
    this.locationId = '';
    this.emailType = '';
  }

  @computed get notReadyMessage() {
    if(!this.emailType) {
      return "Please select an email type.";
    } else if(this.isInvitePlatformType) {
      if(this.recipients.length===0) {
        return "At least one email must be added to the invitation list.";
      }
    } else if(this.isInviteSolutionType) {
      if(!this.solutionId) {
        return "You must select a solution to invite account holders to.";
      }
    } else if(this.isCustomType) {
      let notReadyMessage = "";
      if(this.subject && this.subject.trim().length<5) {
        notReadyMessage += "The subject of the email is required to be at least 5 characters in length. We suggest something a bit longer."
      }
      if(this.body && this.body.trim().length<20) {
        notReadyMessage += "Your message appears to be too short. It should be at least 20 characters in length."
      }
      return notReadyMessage;
    }

    return "";
  }


  @computed get readyToSend() {
    if(!this.sending) {
      if(this.emailType) {
        if(this.isInvitePlatformType) {
          if(this.recipients.length>0) {
            return true;
          }
        } else if(this.isInviteSolutionType) {
          if (this.solutionId) {
            return true;
          }
        } else if(this.isCustomType) {
          if(this.subject && this.subject.trim().length>=5 && this.body && this.body.trim().length>=20) {
            return true;
          }
        }
      }
    }
    return false;
  }

  render() {
    return (
        <HRToolCard title={"Email Sending Tool"}>
          <div className={"hr-email-tool"}>

            <HRFormBlock title={"Email Type"} help={<>
              <p>Select the type of email you wish to send.</p>
              <ul>
                <li><strong>Invite to Platform</strong> is sent to a list of email addresses that you enter. People who are already registered will not be invited again.</li>
                <li><strong>Solution Invite</strong> sends an email to all people who have accounts, but have not registered for a particular solution.</li>
                <li><strong>Custom Message</strong> is sent to every person who has an account.</li>
              </ul>
            </>}>
              <div>
                <label><input type={"radio"} name={"type"} value={EmailType.INVITE_PLATFORM} checked={this.emailType===EmailType.INVITE_PLATFORM} onChange={this.setInvitePlatformType} /> Invite to Platform</label>
                <label><input type={"radio"} name={"type"} value={EmailType.INVITE_SOLUTION}
                              checked={this.emailType === EmailType.INVITE_SOLUTION}
                              onChange={this.setInviteSolutionType}/> Solution Invite</label>
                <label><input type={"radio"} name={"type"} value={EmailType.CUSTOM}
                              checked={this.emailType === EmailType.CUSTOM} onChange={this.setCustomType}/> Custom Message</label>
              </div>
            </HRFormBlock>




            <HRFormBlock show={this.isInviteSolutionType} title={"Solution"} description={"Invite everyone who has an account, but has not yet registered for the selected solution."}>
              <select value={this.solutionId} onChange={(event)=>{ this.solutionId = event.target.value; }}>
                <option value="">Select solution...</option>
                {UIState.dashboardData.data.solutions.map((solution)=> {
                  return <option key={solution._id} value={solution._id}>{solution.name}</option>;
                })}
              </select>
            </HRFormBlock>
            {this.locations && this.locations.length>0 &&
              <>
              {this.locations.length>1 ?
              <HRFormBlock show={this.isInviteSolutionType} title={"Location"} description={"The locations below are associated with this solution. You may optionally narrow this invitation email to a specific solution by selecting that location below."}>
              <select value={this.locationId} onChange={(event)=>{ this.locationId = event.target.value; }}>
                <option value="">All</option>
                {this.locations.map((location)=> {
                  return <option key={location._id} value={location._id}>{location.name}</option>;
                })}
              </select>
            </HRFormBlock>
              :
                <HRFormBlock show={this.isInviteSolutionType} title={"Location"} description={"Only employees in this location will be invited."}>
                  <select><option>{this.locations[0].name}</option></select>
                </HRFormBlock>
              }
              </>
            }


            <HRFormBlock show={this.isInvitePlatformType} title={"Recipients"} help={<div><p>Type or paste a list of email addresses below and then click the add button to add them to
                the recipient list. Emails should be seperated by commas, or be on their own lines. Including
                names with emails will increase deliverability (if the names are correct). If a name
                is included, the email must be between &lt; and &gt;. The list may be mixed,
                here is an example:</p>
                <pre>
                  Jane Doe &lt;jane.doe@yourcompany.com&gt;<br/>
                  bobby.doe@yourcompany.com<br/>
                  John Doe &lt;john.doe@yourcompany.com&gt;
                </pre>
              </div>}>
                <HRCustomRecipients onRecipientsChange={action((recipients)=>{
                  this.recipients = recipients;
                  console.log(recipients);
                })} />
            </HRFormBlock>

            <HRFormBlock show={this.isCustomType} title={"Subject"}>
              <input className={"subject"} type={"text"} value={this.subject} onChange={action((e)=>{this.subject = e.target.value;})} />
            </HRFormBlock>

            <HRFormBlock show={this.isCustomType} title={"Message"}>
              <textarea className={"body"} value={this.body} onChange={action((e)=>{this.body = e.target.value;})}/>
            </HRFormBlock>

            <div className={"hr-send-block"}>
              {/*<p><strong>2/23/2023 @4:53pm</strong> We have temporarily disabled the email feature because our enterprise cloud-based mail service is experiencing a Denial of Service Attack (DDoS),*/}
              {/*which has made sending email unstable. This is not a security breech, there is no need for concern. A DDoS attack is created by sending an overwhelming number of requests to a*/}
              {/*server. This is done in order to create an interruption in business that will cost the target company a great deal of money and time. Important Internet infrastructure*/}
              {/*  businesses are common targets of DDoS attacks, and our mail service is one such business. As soon as this has been resolved, and service has returned to normal, we will re-enable*/}
              {/*the email tool.</p>*/}

              <p>{this.notReadyMessage}</p>
              <button disabled={!this.readyToSend} onClick={this.send}>Send</button>

            </div>

          </div>
        </HRToolCard>
    );
  }
}